/**
 * Drag-to-dismiss for the mobile navigation bottom sheet.
 *
 * On phones (< 750px) the header menu drawer is styled as an iOS-style bottom
 * sheet (see header-drawer.liquid). This module adds the drag gesture to match
 * the currency picker: dragging the grab handle down slides the panel with the
 * finger and dismisses on a far-enough drag or a downward flick, springing back
 * otherwise.
 *
 * It attaches to the existing <header-drawer> element and drives its public
 * close() method — the drawer's own component (header-drawer.js) is left
 * untouched. Closing via the X, backdrop and Esc all keep working; this is a
 * progressive enhancement layered on top.
 *
 * Mechanics mirror assets/currency-sheet.js.
 */

const MOBILE = window.matchMedia('(max-width: 749px)');

/** @param {Element} drawer - a <header-drawer> element */
function wireDrawer(drawer) {
  const grab = /** @type {HTMLElement | null} */ (drawer.querySelector('.menu-drawer__grab'));
  const panel = /** @type {HTMLElement | null} */ (drawer.querySelector('.menu-drawer'));
  const details = /** @type {HTMLDetailsElement | null} */ (drawer.querySelector('details.menu-drawer-container'));

  if (!grab || !panel || !details) return;

  /** @type {number | null} */
  let pointerId = null;
  let startY = 0;
  let currentY = 0;
  let lastY = 0;
  let lastTime = 0;
  let velocity = 0;
  let dragging = false;

  /** @returns {boolean} Whether the sheet can be dragged right now. */
  const isDraggable = () =>
    MOBILE.matches &&
    details.classList.contains('menu-open') &&
    // Don't drag the top-level panel while a full-screen submenu is showing.
    !panel.classList.contains('menu-drawer--has-submenu-opened');

  /** @param {PointerEvent} event */
  const onPointerMove = (event) => {
    if (event.pointerId !== pointerId) return;

    const delta = event.clientY - startY;

    // 6px slop before committing to a drag, so taps stay taps.
    if (!dragging) {
      if (Math.abs(delta) < 6) return;
      dragging = true;
      panel.classList.add('is-dragging');
    }

    const elapsed = event.timeStamp - lastTime;
    if (elapsed > 0) velocity = (event.clientY - lastY) / elapsed;
    lastY = event.clientY;
    lastTime = event.timeStamp;

    currentY = Math.max(0, delta);
    panel.style.transform = `translateY(${currentY}px)`;
  };

  /** @param {PointerEvent} event */
  const onPointerUp = (event) => {
    if (event.pointerId !== pointerId) return;

    pointerId = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);

    panel.classList.remove('is-dragging');

    if (!dragging) return;

    const farEnough = currentY > panel.clientHeight * 0.25;
    const flicked = velocity > 0.5 && currentY > 20;

    if (farEnough || flicked) {
      // Clear the inline offset and let the drawer close: the panel's base
      // `transition: transform` carries it from the finger position down to
      // translateY(100%) as .menu-open is removed.
      panel.style.transform = '';
      if (typeof (/** @type {any} */ (drawer).close) === 'function') {
        /** @type {any} */ (drawer).close();
      } else {
        details.classList.remove('menu-open');
      }
    } else {
      // Spring back to the open position.
      panel.classList.add('is-settling');
      panel.style.transform = '';
      const settle = () => panel.classList.remove('is-settling');
      panel.addEventListener('transitionend', settle, { once: true });
      // Fallback: under reduced motion the transition never fires.
      setTimeout(settle, 300);
    }
  };

  /** @param {PointerEvent} event */
  const onPointerDown = (event) => {
    if (pointerId !== null || !isDraggable()) return;

    pointerId = event.pointerId;
    startY = lastY = event.clientY;
    lastTime = event.timeStamp;
    velocity = 0;
    currentY = 0;
    dragging = false;

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  };

  grab.addEventListener('pointerdown', onPointerDown);

  // Close the sheet once the user picks a menu option. Only leaf links (real
  // navigations) close it — submenu toggles are <summary>/buttons, which stay.
  panel.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (!MOBILE.matches || !details.classList.contains('menu-open')) return;

    const link = event.target.closest('a[href]');
    if (!link || !panel.contains(link)) return;

    if (typeof (/** @type {any} */ (drawer).close) === 'function') {
      /** @type {any} */ (drawer).close();
    } else {
      details.classList.remove('menu-open');
    }
  });
}

for (const drawer of document.querySelectorAll('header-drawer')) {
  wireDrawer(drawer);
}
